import { useCallback, useEffect, useMemo,useRef, useState } from 'react';

import { supabase } from '../services/supabaseClient';

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isServerReachable, setIsServerReachable] = useState(true);
  const [isCheckingConnectivity, setIsCheckingConnectivity] = useState(false);
  
  // ✅ NOVO: Refs para evitar verificações desnecessárias
  const lastCheckRef = useRef<number>(0);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // ✅ Função para verificar se o servidor está realmente acessível
  const checkServerConnectivity = useCallback(async (forceCheck = false) => {
    // ✅ Evitar verificações muito frequentes
    const now = Date.now();
    if (!forceCheck && (isCheckingConnectivity || (now - lastCheckRef.current < 5000))) {
      return;
    }
    
    try {
      setIsCheckingConnectivity(true);
      lastCheckRef.current = now;
      
      // Tentar fazer uma requisição simples para o Supabase
      const { error } = await supabase
        .from('obras')
        .select('id')
        .limit(1);
      
      // Se não há erro ou o erro é de permissão (que indica conectividade), considerar online
      const reachable = !error || error.code === 'PGRST116';
      
      // ✅ Só atualizar se o valor realmente mudou
      setIsServerReachable(prev => {
        if (prev !== reachable) {
          console.log(`🌐 Status do servidor mudou: ${prev} -> ${reachable}`);
          return reachable;
        }
        return prev;
      });
      

    } catch (error) {
      setIsServerReachable(prev => {
        if (prev !== false) {
          console.log('🌐 Servidor inacessível');
          return false;
        }
        return prev;
      });
    } finally {
      setIsCheckingConnectivity(false);
    }
  }, [isCheckingConnectivity]);

  // ✅ Função para forçar verificação de conectividade
  const forceCheckConnectivity = useCallback(() => {
    checkServerConnectivity(true);
  }, [checkServerConnectivity]);

  useEffect(() => {
    function handleOnline() {
      console.log('🌐 Navegador online');
      setIsOnline(true);
      // Verificar conectividade real quando volta online
      checkServerConnectivity();
    }

    function handleOffline() {
      console.log('🌐 Navegador offline');
      setIsOnline(false);
      setIsServerReachable(false);
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // ✅ Verificar conectividade inicial
    if (navigator.onLine) {
      checkServerConnectivity();
    }

    // ✅ Verificar conectividade periodicamente quando online (com intervalo maior)
    checkIntervalRef.current = setInterval(() => {
      if (navigator.onLine) {
        checkServerConnectivity();
      }
    }, 60000); // ✅ Aumentado para 60 segundos para reduzir verificações

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkServerConnectivity]);

  // ✅ Retornar objeto memoizado para manter referência estável
  const value = useMemo(() => ({
    isOnline: isOnline && isServerReachable,
    isCheckingConnectivity,
    forceCheckConnectivity,
    networkStatus: {
      navigatorOnline: isOnline,
      serverReachable: isServerReachable,
    },
  }), [isOnline, isServerReachable, isCheckingConnectivity, forceCheckConnectivity]);

  return value;
}